import { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useNavigationType } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { Toaster } from "@/components/ui/sonner";
import { HelmetProvider } from "react-helmet-async";
import axios from "axios";
import { API_URL } from "@/config";
import { trackPageVisit, initErrorTracking, trackPageEntry } from "@/utils/analytics";
import { prefetchAllData } from "@/utils/dataPrefetcher";
import DynamicSEO from "@/components/DynamicSEO";
import ErrorBoundary from "@/components/ErrorBoundary";
import MaintenanceBanner from "@/components/MaintenanceBanner";
import ConnectionStatus from "@/components/ConnectionStatus";
import "@/utils/apiResilience"; // Auto-retry for all API calls

// App version - UPDATE THIS ON EACH DEPLOY
const APP_VERSION = "2026.02.06.001";

// Disable caching for all API requests
axios.defaults.headers.common['Cache-Control'] = 'no-cache, no-store, must-revalidate';
axios.defaults.headers.common['Pragma'] = 'no-cache';
axios.defaults.headers.common['Expires'] = '0';

// Version check - only updates stored version, no automatic refresh
const checkAndUpdateVersion = async () => {
  try {
    const response = await fetch(`${API_URL}/api/version?t=${Date.now()}`, {
      cache: 'no-store'
    });
    
    if (response.ok) {
      const data = await response.json();
      const serverVersion = data.version;
      
      console.log(`📦 App: ${APP_VERSION}, Server: ${serverVersion}`);
      
      // Just store the version, don't refresh automatically
      localStorage.setItem('app_version', serverVersion);
    }
  } catch (error) {
    console.log('Version check failed:', error);
  }
};

// Run version check on load (no automatic refresh)
checkAndUpdateVersion();

// Prefetch critical page data from server immediately
prefetchAllData();

// Removed: setInterval that was causing unexpected page refreshes
// The version check will run once on page load instead

// Auto-retry lazy imports on chunk load failure
const lazyWithRetry = (importFn) => {
  return lazy(() =>
    importFn().catch(() => {
      // Retry once after a short delay
      return new Promise(resolve => setTimeout(resolve, 1000))
        .then(() => importFn())
        .catch(() => {
          // If retry fails, reload the page silently
          window.location.reload();
          return { default: () => null };
        });
    })
  );
};

// LandingPage & PricingPage - loaded directly for instant display
import LandingPage from "@/pages/LandingPage";
import PricingPage from "@/pages/PricingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";

// الصفحات الأكثر زيارة - تُحمّل مباشرة للدخول الفوري
import TestsPage from "@/pages/TestsPage";
import LessonsPage from "@/pages/LessonsPage";
import TrafficSignsPage from "@/pages/TrafficSignsPage";
import QuickTipsPage from "@/pages/QuickTipsPage";
import VideosPage from "@/pages/VideosPage";
import DashboardPage from "@/pages/DashboardPage";
import LessonDetailPage from "@/pages/LessonDetailPage";
import TrafficSignDetailPage from "@/pages/TrafficSignDetailPage";
import QuickTipDetailPage from "@/pages/QuickTipDetailPage";

// الصفحات الأقل زيارة - تُحمّل عند الطلب فقط
const VerifyEmailPage = lazyWithRetry(() => import("@/pages/VerifyEmailPage"));
const ForgotPasswordPage = lazyWithRetry(() => import("@/pages/ForgotPasswordPage"));
const AuthCallback = lazyWithRetry(() => import("@/pages/AuthCallback"));
const InteractiveMapPage = lazyWithRetry(() => import("@/pages/InteractiveMapPage"));
const QuestionDetailPage = lazyWithRetry(() => import("@/pages/QuestionDetailPage"));
const AssistantPage = lazyWithRetry(() => import(/* webpackPrefetch: true */ "@/pages/AssistantPage"));
const SupportPage = lazyWithRetry(() => import(/* webpackPrefetch: true */ "@/pages/SupportPage"));
const PaymentSuccessPage = lazyWithRetry(() => import("@/pages/PaymentSuccessPage"));
const SettingsPage = lazyWithRetry(() => import(/* webpackPrefetch: true */ "@/pages/SettingsPage"));
const AdminPanelPage = lazyWithRetry(() => import(/* webpackPrefetch: true */ "@/pages/AdminPanelPage"));
const PrivacyPolicyPage = lazyWithRetry(() => import("@/pages/PrivacyPolicyPage"));
const TermsOfServicePage = lazyWithRetry(() => import("@/pages/TermsOfServicePage"));
const RefundPolicyPage = lazyWithRetry(() => import("@/pages/RefundPolicyPage"));
const DisclaimerPage = lazyWithRetry(() => import("@/pages/DisclaimerPage"));
const CookiesPolicyPage = lazyWithRetry(() => import("@/pages/CookiesPolicyPage"));
const RenewSubscriptionPage = lazyWithRetry(() => import("@/pages/RenewSubscriptionPage"));
const ReviewsPage = lazyWithRetry(() => import(/* webpackPrefetch: true */ "@/pages/ReviewsPage"));
const KorkortsteoriPage = lazyWithRetry(() => import("@/pages/SEOLandingPage").then(m => ({ default: m.KorkortsteoriPage })));
const TeoriprovPage = lazyWithRetry(() => import("@/pages/SEOLandingPage").then(m => ({ default: m.TeoriprovPage })));
const TaxikortSEOPage = lazyWithRetry(() => import("@/pages/TaxikortSEOPage"));
const BKorkortSEOPage = lazyWithRetry(() => import("@/pages/BKorkortSEOPage"));
const ShahadatSawakaPage = lazyWithRetry(() => import("@/pages/ArabicSEOPage").then(m => ({ default: m.ShahadatSawakaPage })));
const ImtihanSawakaPage = lazyWithRetry(() => import("@/pages/ArabicSEOPage").then(m => ({ default: m.ImtihanSawakaPage })));
const IsharatMururPage = lazyWithRetry(() => import("@/pages/ArabicSEOPage").then(m => ({ default: m.IsharatMururPage })));

const KeywordLandingPage = lazyWithRetry(() => import("@/pages/KeywordLandingPage"));
const KorkortsteoriSEO = lazyWithRetry(() => import("@/pages/MultiLangSEOPage").then(m => ({ default: m.KorkortsteoriSEO })));
const TeoriArabiskaSEO = lazyWithRetry(() => import("@/pages/MultiLangSEOPage").then(m => ({ default: m.TeoriArabiskaSEO })));
const DrivingTheorySwedenSEO = lazyWithRetry(() => import("@/pages/MultiLangSEOPage").then(m => ({ default: m.DrivingTheorySwedenSEO })));
const EhliyetTeorisiSEO = lazyWithRetry(() => import("@/pages/MultiLangSEOPage").then(m => ({ default: m.EhliyetTeorisiSEO })));
const TeoriFarsiSEO = lazyWithRetry(() => import("@/pages/MultiLangSEOPage").then(m => ({ default: m.TeoriFarsiSEO })));
const AragtiWadistaSEO = lazyWithRetry(() => import("@/pages/MultiLangSEOPage").then(m => ({ default: m.AragtiWadistaSEO })));
const PrawoJazdySzwecjaSEO = lazyWithRetry(() => import("@/pages/MultiLangSEOPage").then(m => ({ default: m.PrawoJazdySzwecjaSEO })));
const TeoriaConduccionSueciaSEO = lazyWithRetry(() => import("@/pages/MultiLangSEOPage").then(m => ({ default: m.TeoriaConduccionSueciaSEO })));
const FahrtheorieSchwedenSEO = lazyWithRetry(() => import("@/pages/MultiLangSEOPage").then(m => ({ default: m.FahrtheorieSchwedenSEO })));
const ImportantWordsPage = lazyWithRetry(() => import(/* webpackPrefetch: true */ "@/pages/ImportantWordsPage"));
const HowToUsePage = lazyWithRetry(() => import(/* webpackPrefetch: true */ "@/pages/HowToUsePage"));
const ArticlesPage = lazyWithRetry(() => import(/* webpackPrefetch: true */ "@/pages/ArticlesPage"));
const TrafficRulesPage = lazyWithRetry(() => import(/* webpackPrefetch: true */ "@/pages/TrafficRulesPage"));
const NotFoundPage = lazyWithRetry(() => import("@/pages/NotFoundPage"));
const CookieConsent = lazyWithRetry(() => import("@/components/CookieConsent"));
const ChatWidget = lazyWithRetry(() => import("@/components/ChatWidget"));
const TrialHourCountdown = lazyWithRetry(() => import("@/components/TrialHourCountdown"));
import LocalizedRoute from "@/components/LocalizedRoute";
import i18n from "@/i18n";
import { CITY_ROUTES } from "@/data/cityPages";

import { API } from '@/config';

// مكون لإعادة التوجيه مع الحفاظ على المتغيرات
const RedirectWithParams = ({ to }) => {
  const params = window.location.pathname.split('/').pop();
  return <Navigate to={`${to}/${params}`} replace />;
};

// /sv/<swedish-path> → set Swedish and open the unprefixed Swedish URL (e.g. /sv/trafikmarken → /trafikmarken)
const SvPrefixRedirect = () => {
  const { pathname, search } = useLocation();
  if (i18n.language !== 'sv') {
    localStorage.setItem('i18nextLng', 'sv');
    i18n.changeLanguage('sv');
  }
  const target = pathname.replace(/^\/sv(?=\/|$)/, '') || '/';
  return <Navigate to={`${target}${search}`} replace />;
};

// مكون لإعادة التوجيه مع الحفاظ على query parameters
const RedirectWithQuery = ({ to }) => {
  const search = window.location.search;
  return <Navigate to={`${to}${search}`} replace />;
};

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-500 border-t-transparent"></div>
      </div>
    );
  }
  
  return isAuthenticated ? children : <Navigate to="/logga-in" replace />;
};

// مكون لحماية الصفحات من المستخدمين المجانيين (يسمح فقط للمشتركين)
const SubscribedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-500 border-t-transparent"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/logga-in" replace />;
  }
  
  // السماح للمدير دائماً
  if (user?.is_admin || user?.role === 'super_admin') {
    return children;
  }
  
  // السماح للمشتركين فقط — سواء اشتراك B-körkort أو Taxikort يفتح لك الداشبورد
  if (user?.subscription_status === 'active' || user?.taxi_subscription_status === 'active') {
    return children;
  }
  
  // المستخدم المجاني - إظهار صفحة الترقية
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-3xl shadow-2xl p-8 text-center">
        <div className="w-20 h-20 mx-auto mb-6 bg-amber-100 rounded-full flex items-center justify-center">
          <span className="text-4xl">🔒</span>
        </div>
        <h2 className="text-2xl font-bold text-slate-900 mb-3">
          {i18n.language === 'ar' 
            ? 'هذا المحتوى للمشتركين فقط'
            : i18n.language === 'sv'
            ? 'Detta innehåll är endast för prenumeranter'
            : 'This content is for subscribers only'}
        </h2>
        <p className="text-slate-600 mb-6">
          {i18n.language === 'ar' 
            ? `لديك ${user?.questions_remaining || 0} سؤال مجاني متبقي. يمكنك استخدامها في صفحة الاختبارات، أو اشترك للوصول لجميع المحتوى.`
            : i18n.language === 'sv'
            ? `Du har ${user?.questions_remaining || 0} gratis frågor kvar. Använd dem på testsidan, eller prenumerera för full åtkomst.`
            : `You have ${user?.questions_remaining || 0} free questions left. Use them on the tests page, or subscribe for full access.`}
        </p>
        <div className="space-y-3">
          <button
            onClick={() => navigate('/priser')}
            className="w-full py-3 px-6 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-bold rounded-xl hover:from-emerald-600 hover:to-emerald-700 transition-all shadow-lg"
          >
            {i18n.language === 'ar' ? 'اشترك الآن 🚀' : i18n.language === 'sv' ? 'Prenumerera nu 🚀' : 'Subscribe Now 🚀'}
          </button>
          <button
            onClick={() => navigate('/teoriprov')}
            className="w-full py-3 px-6 border-2 border-slate-300 text-slate-700 font-semibold rounded-xl hover:bg-slate-50 transition-all"
          >
            {i18n.language === 'ar' ? 'جرّب الأسئلة المجانية' : i18n.language === 'sv' ? 'Prova gratis frågor' : 'Try Free Questions'}
          </button>
        </div>
      </div>
    </div>
  );
};


// Redirect unknown routes to home page based on current language
const NotFoundRedirect = () => {
  return <NotFoundPage />;
};

const AppContent = () => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const { isAuthenticated, token } = useAuth();

  // Post-login auto-checkout for the paid trial-hour pass: when a logged-out
  // visitor clicks "Activate for 29 kr" we stash a flag in localStorage and
  // route them through sign-up. As soon as they're authenticated we clear
  // the flag and open the Stripe checkout in the same tab.
  useEffect(() => {
    if (!isAuthenticated || !token) return;
    let pending = null;
    let pendingLic = 'b_korkort';
    try {
      pending = localStorage.getItem('trial_hour_pending');
      pendingLic = localStorage.getItem('trial_hour_pending_license') || 'b_korkort';
    } catch (_) {}
    if (pending !== '1') return;
    try {
      localStorage.removeItem('trial_hour_pending');
      localStorage.removeItem('trial_hour_pending_license');
    } catch (_) {}
    (async () => {
      try {
        const { data } = await import('axios').then(m => m.default.post(
          `${API}/payments/trial-hour/checkout?licenseType=${encodeURIComponent(pendingLic)}`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        ));
        if (data?.url) window.location.href = data.url;
      } catch (_) { /* silent — user can retry from the offer card */ }
    })();
  }, [isAuthenticated, token]);

  useEffect(() => {
    // Set document direction based on language
    document.documentElement.dir = (i18n.language === 'ar' || i18n.language === 'fa') ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  // Analytics: track page views on route changes
  useEffect(() => {
    initErrorTracking();
    
    // Heartbeat: ping server every 4 minutes to keep it awake
    const heartbeat = setInterval(() => {
      fetch(`${API}/health`, { method: 'HEAD' }).catch(() => {});
    }, 240000); // 4 دقائق
    
    return () => clearInterval(heartbeat);
  }, []);

  useEffect(() => {
    trackPageEntry(location.pathname);
    trackPageVisit(location.pathname);
  }, [location.pathname]);

  const navType = useNavigationType();
  useEffect(() => {
    if (location.hash || navType === 'POP') return;
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [location.pathname, location.hash, navType]);

  return (
    <div className="App min-h-screen bg-slate-50">
      <MaintenanceBanner />
      <DynamicSEO />
      <main>
      <Suspense fallback={null}>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/logga-in" element={<LoginPage />} />
        <Route path="/registrera" element={<RegisterPage />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/verifiera-epost" element={<VerifyEmailPage />} />
        <Route path="/glomt-losenord" element={<ForgotPasswordPage />} />
        <Route path="/priser" element={<PricingPage />} />
        {/* Dedicated SEO landing for the Taxi license — Swedish content only.
            Aliases keep the URL you already see in ads / social discoverable. */}
        <Route path="/taxikort" element={<TaxikortSEOPage />} />
        <Route path="/taxikort-teoriprov" element={<Navigate to="/taxikort" replace />} />
        <Route path="/taxiforarlegitimation" element={<Navigate to="/taxikort" replace />} />
        <Route path="/betalning/lyckades" element={<PaymentSuccessPage />} />
        
        {/* جميع الصفحات متاحة للمستخدم المسجّل - لكن مع قيود على الاستخدام */}
        {/* Primary route — high search volume Swedish term (~22k/mo) */}
        <Route path="/teoriprov" element={<TestsPage />} />
        {/* Legacy alias — 301-style redirect preserves any existing backlinks */}
        <Route path="/ovningsprov" element={<Navigate to="/teoriprov" replace />} />
        <Route path="/oversikt" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
        <Route path="/teorilektioner" element={<LessonsPage />} />
        <Route path="/teorilektioner/:id" element={<LessonDetailPage />} />
        <Route path="/trafikmarken" element={<TrafficSignsPage />} />
        <Route path="/trafikmarken/:id" element={<TrafficSignDetailPage />} />
        <Route path="/interaktiv-karta" element={<InteractiveMapPage />} />
        <Route path="/interactive-map" element={<Navigate to="/interaktiv-karta" replace />} />
        <Route path="/viktiga-ord" element={<ImportantWordsPage />} />
        <Route path="/hur-man-anvander" element={<HowToUsePage />} />
        <Route path="/artiklar" element={<ArticlesPage />} />
        <Route path="/trafikregler" element={<TrafficRulesPage />} />
        <Route path="/snabba-rad" element={<QuickTipsPage />} />
        <Route path="/teorivideor" element={<VideosPage />} />
        <Route path="/videor" element={<VideosPage />} />
        <Route path="/snabba-rad/:id" element={<QuickTipDetailPage />} />
        <Route path="/teorifragor/:slug" element={<QuestionDetailPage />} />
        <Route path="/ai-assistent" element={<AssistantPage />} />
        <Route path="/almosaed" element={<Navigate to="/ai-assistent" replace />} />
        <Route path="/installningar" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
        
        {/* صفحات عامة */}
        <Route path="/support" element={<SupportPage />} />
        <Route path="/recensioner" element={<ReviewsPage />} />
        <Route path="/reviews" element={<ReviewsPage />} />
        <Route path="/en/reviews" element={<LocalizedRoute lang="en"><ReviewsPage /></LocalizedRoute>} />
        <Route path="/ar/taqyimat" element={<LocalizedRoute lang="ar"><ReviewsPage /></LocalizedRoute>} />
        <Route path="/tr/yorumlar" element={<LocalizedRoute lang="tr"><ReviewsPage /></LocalizedRoute>} />
        <Route path="/fa/nazarat" element={<LocalizedRoute lang="fa"><ReviewsPage /></LocalizedRoute>} />
        <Route path="/so/dib-u-celinta" element={<LocalizedRoute lang="so"><ReviewsPage /></LocalizedRoute>} />
        <Route path="/de/bewertungen" element={<LocalizedRoute lang="de"><ReviewsPage /></LocalizedRoute>} />
        <Route path="/es/resenas" element={<LocalizedRoute lang="es"><ReviewsPage /></LocalizedRoute>} />
        <Route path="/pl/opinie" element={<LocalizedRoute lang="pl"><ReviewsPage /></LocalizedRoute>} />
        <Route path="/integritet" element={<PrivacyPolicyPage />} />
        <Route path="/villkor" element={<TermsOfServicePage />} />
        <Route path="/aterbetalning" element={<RefundPolicyPage />} />
        <Route path="/ansvarsfriskrivning" element={<DisclaimerPage />} />
        <Route path="/cookies" element={<CookiesPolicyPage />} />
        
        {/* SEO Landing Pages - Swedish */}
        <Route path="/korkortsteori" element={<BKorkortSEOPage />} />
        <Route path="/teoritenta" element={<KeywordLandingPage />} />
        <Route path="/ovningsprov-korkort" element={<KeywordLandingPage />} />
        <Route path="/korkortstest" element={<KeywordLandingPage />} />
        <Route path="/ta-korkort" element={<KeywordLandingPage />} />
        {CITY_ROUTES.map((p) => <Route key={p} path={p} element={<KeywordLandingPage />} />)}
        <Route path="/teoriprov-gratis" element={<KeywordLandingPage />} />
        <Route path="/korkortsfragor" element={<KeywordLandingPage />} />
        
        {/* SEO Landing Pages - Arabic (تيوري) */}
        <Route path="/teori-arabiska" element={<KeywordLandingPage />} />
        <Route path="/shahadat-alsawaka" element={<ShahadatSawakaPage />} />
        <Route path="/imtihan-alsawaka" element={<ImtihanSawakaPage />} />
        <Route path="/isharat-almurur" element={<IsharatMururPage />} />
        
        {/* SEO Landing Pages - Other Languages */}
        <Route path="/driving-theory-sweden" element={<KeywordLandingPage />} />
        <Route path="/ehliyet-teorisi" element={<KeywordLandingPage />} />
        <Route path="/teori-farsi" element={<KeywordLandingPage />} />
        <Route path="/aragti-wadista" element={<KeywordLandingPage />} />
        <Route path="/prawo-jazdy-szwecja" element={<KeywordLandingPage />} />
        <Route path="/teoria-conduccion-suecia" element={<KeywordLandingPage />} />
        <Route path="/fahrtheorie-schweden" element={<KeywordLandingPage />} />
        
        {/* Localized Routes with short codes - /en/, /ar/, /tr/, etc. */}
        {/* English Routes - All Pages */}
        <Route path="/en" element={<LocalizedRoute lang="en"><LandingPage /></LocalizedRoute>} />
        <Route path="/en/login" element={<LocalizedRoute lang="en"><LoginPage /></LocalizedRoute>} />
        <Route path="/en/register" element={<LocalizedRoute lang="en"><RegisterPage /></LocalizedRoute>} />
        <Route path="/en/forgot-password" element={<LocalizedRoute lang="en"><ForgotPasswordPage /></LocalizedRoute>} />
        <Route path="/en/pricing" element={<LocalizedRoute lang="en"><PricingPage /></LocalizedRoute>} />
        <Route path="/en/assistant" element={<LocalizedRoute lang="en"><AssistantPage /></LocalizedRoute>} />
        <Route path="/en/lessons" element={<LocalizedRoute lang="en"><LessonsPage /></LocalizedRoute>} />
        <Route path="/en/lessons/:id" element={<LocalizedRoute lang="en"><LessonDetailPage /></LocalizedRoute>} />
        <Route path="/en/tests" element={<LocalizedRoute lang="en"><TestsPage /></LocalizedRoute>} />
        <Route path="/en/traffic-signs" element={<LocalizedRoute lang="en"><TrafficSignsPage /></LocalizedRoute>} />
        <Route path="/en/traffic-signs/:id" element={<LocalizedRoute lang="en"><TrafficSignDetailPage /></LocalizedRoute>} />
        <Route path="/en/quick-tips" element={<LocalizedRoute lang="en"><QuickTipsPage /></LocalizedRoute>} />
        <Route path="/en/interactive-map" element={<LocalizedRoute lang="en"><InteractiveMapPage /></LocalizedRoute>} />
        <Route path="/en/important-words" element={<LocalizedRoute lang="en"><ImportantWordsPage /></LocalizedRoute>} />
        <Route path="/en/how-to-use" element={<LocalizedRoute lang="en"><HowToUsePage /></LocalizedRoute>} />
        <Route path="/en/articles" element={<LocalizedRoute lang="en"><ArticlesPage /></LocalizedRoute>} />
        <Route path="/en/traffic-rules" element={<LocalizedRoute lang="en"><TrafficRulesPage /></LocalizedRoute>} />
        <Route path="/en/quick-tips/:id" element={<LocalizedRoute lang="en"><QuickTipDetailPage /></LocalizedRoute>} />
        <Route path="/en/theory-questions/:slug" element={<LocalizedRoute lang="en"><QuestionDetailPage /></LocalizedRoute>} />
        <Route path="/en/support" element={<LocalizedRoute lang="en"><SupportPage /></LocalizedRoute>} />
        <Route path="/en/settings" element={<LocalizedRoute lang="en"><ProtectedRoute><SettingsPage /></ProtectedRoute></LocalizedRoute>} />
        <Route path="/en/privacy" element={<LocalizedRoute lang="en"><PrivacyPolicyPage /></LocalizedRoute>} />
        <Route path="/en/terms" element={<LocalizedRoute lang="en"><TermsOfServicePage /></LocalizedRoute>} />
        
        {/* Arabic Routes - All Pages (Arabic transliterated paths) */}
        <Route path="/ar" element={<LocalizedRoute lang="ar"><LandingPage /></LocalizedRoute>} />
        <Route path="/ar/tsjeel-dkhool" element={<LocalizedRoute lang="ar"><LoginPage /></LocalizedRoute>} />
        <Route path="/ar/inshaa-hsab" element={<LocalizedRoute lang="ar"><RegisterPage /></LocalizedRoute>} />
        <Route path="/ar/nseet-klmat-almroor" element={<LocalizedRoute lang="ar"><ForgotPasswordPage /></LocalizedRoute>} />
        <Route path="/ar/alasaar" element={<LocalizedRoute lang="ar"><PricingPage /></LocalizedRoute>} />
        <Route path="/ar/almosaed" element={<LocalizedRoute lang="ar"><AssistantPage /></LocalizedRoute>} />
        <Route path="/ar/doross" element={<LocalizedRoute lang="ar"><LessonsPage /></LocalizedRoute>} />
        <Route path="/ar/doross/:id" element={<LocalizedRoute lang="ar"><LessonDetailPage /></LocalizedRoute>} />
        <Route path="/ar/ikhtibarat" element={<LocalizedRoute lang="ar"><TestsPage /></LocalizedRoute>} />
        <Route path="/ar/isharat-almroor" element={<LocalizedRoute lang="ar"><TrafficSignsPage /></LocalizedRoute>} />
        <Route path="/ar/isharat-almroor/:id" element={<LocalizedRoute lang="ar"><TrafficSignDetailPage /></LocalizedRoute>} />
        <Route path="/ar/nasaeh-sareea" element={<LocalizedRoute lang="ar"><QuickTipsPage /></LocalizedRoute>} />
        <Route path="/ar/alkharita-altafaeulia" element={<LocalizedRoute lang="ar"><InteractiveMapPage /></LocalizedRoute>} />
        <Route path="/ar/kalimat-hama" element={<LocalizedRoute lang="ar"><ImportantWordsPage /></LocalizedRoute>} />
        <Route path="/ar/tariqat-alistkhdam" element={<LocalizedRoute lang="ar"><HowToUsePage /></LocalizedRoute>} />
        <Route path="/ar/maqalat" element={<LocalizedRoute lang="ar"><ArticlesPage /></LocalizedRoute>} />
        <Route path="/ar/qawanin-almroor" element={<LocalizedRoute lang="ar"><TrafficRulesPage /></LocalizedRoute>} />
        <Route path="/ar/nasaeh-sareea/:id" element={<LocalizedRoute lang="ar"><QuickTipDetailPage /></LocalizedRoute>} />
        <Route path="/ar/aselat-alnazaria/:slug" element={<LocalizedRoute lang="ar"><QuestionDetailPage /></LocalizedRoute>} />
        <Route path="/ar/daem" element={<LocalizedRoute lang="ar"><SupportPage /></LocalizedRoute>} />
        <Route path="/ar/lawhat-altahkum" element={<LocalizedRoute lang="ar"><ProtectedRoute><DashboardPage /></ProtectedRoute></LocalizedRoute>} />
        <Route path="/ar/aladaadaat" element={<LocalizedRoute lang="ar"><ProtectedRoute><SettingsPage /></ProtectedRoute></LocalizedRoute>} />
        <Route path="/ar/alkhsosiya" element={<LocalizedRoute lang="ar"><PrivacyPolicyPage /></LocalizedRoute>} />
        <Route path="/ar/alshroot" element={<LocalizedRoute lang="ar"><TermsOfServicePage /></LocalizedRoute>} />
        {/* Keep old English paths for backwards compatibility */}
        <Route path="/ar/login" element={<Navigate to="/ar/tsjeel-dkhool" replace />} />
        <Route path="/ar/register" element={<Navigate to="/ar/inshaa-hsab" replace />} />
        <Route path="/ar/lessons" element={<Navigate to="/ar/doross" replace />} />
        <Route path="/ar/tests" element={<Navigate to="/ar/ikhtibarat" replace />} />
        <Route path="/ar/assistant" element={<Navigate to="/ar/almosaed" replace />} />
        <Route path="/ar/pricing" element={<Navigate to="/ar/alasaar" replace />} />
        <Route path="/ar/support" element={<Navigate to="/ar/daem" replace />} />
        <Route path="/ar/dashboard" element={<Navigate to="/ar/lawhat-altahkum" replace />} />
        <Route path="/ar/traffic-signs" element={<Navigate to="/ar/isharat-almroor" replace />} />
        <Route path="/ar/quick-tips" element={<Navigate to="/ar/nasaeh-sareea" replace />} />
        <Route path="/ar/settings" element={<Navigate to="/ar/aladaadaat" replace />} />
        <Route path="/ar/privacy" element={<Navigate to="/ar/alkhsosiya" replace />} />
        <Route path="/ar/terms" element={<Navigate to="/ar/alshroot" replace />} />
        
        {/* Turkish Routes - All Pages (Turkish paths) */}
        <Route path="/tr" element={<LocalizedRoute lang="tr"><LandingPage /></LocalizedRoute>} />
        <Route path="/tr/giris" element={<LocalizedRoute lang="tr"><LoginPage /></LocalizedRoute>} />
        <Route path="/tr/kayit" element={<LocalizedRoute lang="tr"><RegisterPage /></LocalizedRoute>} />
        <Route path="/tr/sifremi-unuttum" element={<LocalizedRoute lang="tr"><ForgotPasswordPage /></LocalizedRoute>} />
        <Route path="/tr/fiyatlar" element={<LocalizedRoute lang="tr"><PricingPage /></LocalizedRoute>} />
        <Route path="/tr/asistan" element={<LocalizedRoute lang="tr"><AssistantPage /></LocalizedRoute>} />
        <Route path="/tr/dersler" element={<LocalizedRoute lang="tr"><LessonsPage /></LocalizedRoute>} />
        <Route path="/tr/dersler/:id" element={<LocalizedRoute lang="tr"><LessonDetailPage /></LocalizedRoute>} />
        <Route path="/tr/testler" element={<LocalizedRoute lang="tr"><TestsPage /></LocalizedRoute>} />
        <Route path="/tr/trafik-isaretleri" element={<LocalizedRoute lang="tr"><TrafficSignsPage /></LocalizedRoute>} />
        <Route path="/tr/trafik-isaretleri/:id" element={<LocalizedRoute lang="tr"><TrafficSignDetailPage /></LocalizedRoute>} />
        <Route path="/tr/hizli-ipuclari" element={<LocalizedRoute lang="tr"><QuickTipsPage /></LocalizedRoute>} />
        <Route path="/tr/etkilesimli-harita" element={<LocalizedRoute lang="tr"><InteractiveMapPage /></LocalizedRoute>} />
        <Route path="/tr/onemli-kelimeler" element={<LocalizedRoute lang="tr"><ImportantWordsPage /></LocalizedRoute>} />
        <Route path="/tr/nasil-kullanilir" element={<LocalizedRoute lang="tr"><HowToUsePage /></LocalizedRoute>} />
        <Route path="/tr/makaleler" element={<LocalizedRoute lang="tr"><ArticlesPage /></LocalizedRoute>} />
        <Route path="/tr/trafik-kurallari" element={<LocalizedRoute lang="tr"><TrafficRulesPage /></LocalizedRoute>} />
        <Route path="/tr/hizli-ipuclari/:id" element={<LocalizedRoute lang="tr"><QuickTipDetailPage /></LocalizedRoute>} />
        <Route path="/tr/teori-sorulari/:slug" element={<LocalizedRoute lang="tr"><QuestionDetailPage /></LocalizedRoute>} />
        <Route path="/tr/destek" element={<LocalizedRoute lang="tr"><SupportPage /></LocalizedRoute>} />
        <Route path="/tr/kontrol-paneli" element={<LocalizedRoute lang="tr"><ProtectedRoute><DashboardPage /></ProtectedRoute></LocalizedRoute>} />
        <Route path="/tr/ayarlar" element={<LocalizedRoute lang="tr"><ProtectedRoute><SettingsPage /></ProtectedRoute></LocalizedRoute>} />
        <Route path="/tr/gizlilik" element={<LocalizedRoute lang="tr"><PrivacyPolicyPage /></LocalizedRoute>} />
        <Route path="/tr/sartlar" element={<LocalizedRoute lang="tr"><TermsOfServicePage /></LocalizedRoute>} />
        {/* Turkish backwards compatibility redirects */}
        <Route path="/tr/login" element={<Navigate to="/tr/giris" replace />} />
        <Route path="/tr/register" element={<Navigate to="/tr/kayit" replace />} />
        <Route path="/tr/lessons" element={<Navigate to="/tr/dersler" replace />} />
        <Route path="/tr/tests" element={<Navigate to="/tr/testler" replace />} />
        <Route path="/tr/assistant" element={<Navigate to="/tr/asistan" replace />} />
        <Route path="/tr/pricing" element={<Navigate to="/tr/fiyatlar" replace />} />
        <Route path="/tr/support" element={<Navigate to="/tr/destek" replace />} />
        <Route path="/tr/dashboard" element={<Navigate to="/tr/kontrol-paneli" replace />} />
        <Route path="/tr/traffic-signs" element={<Navigate to="/tr/trafik-isaretleri" replace />} />
        <Route path="/tr/quick-tips" element={<Navigate to="/tr/hizli-ipuclari" replace />} />
        <Route path="/tr/settings" element={<Navigate to="/tr/ayarlar" replace />} />
        <Route path="/tr/privacy" element={<Navigate to="/tr/gizlilik" replace />} />
        <Route path="/tr/terms" element={<Navigate to="/tr/sartlar" replace />} />
        
        {/* Persian Routes - All Pages (Farsi transliterated paths) */}
        <Route path="/fa" element={<LocalizedRoute lang="fa"><LandingPage /></LocalizedRoute>} />
        <Route path="/fa/vorod" element={<LocalizedRoute lang="fa"><LoginPage /></LocalizedRoute>} />
        <Route path="/fa/sabt-nam" element={<LocalizedRoute lang="fa"><RegisterPage /></LocalizedRoute>} />
        <Route path="/fa/faramoshi-ramz" element={<LocalizedRoute lang="fa"><ForgotPasswordPage /></LocalizedRoute>} />
        <Route path="/fa/gheymat-ha" element={<LocalizedRoute lang="fa"><PricingPage /></LocalizedRoute>} />
        <Route path="/fa/dastiar" element={<LocalizedRoute lang="fa"><AssistantPage /></LocalizedRoute>} />
        <Route path="/fa/dars-ha" element={<LocalizedRoute lang="fa"><LessonsPage /></LocalizedRoute>} />
        <Route path="/fa/dars-ha/:id" element={<LocalizedRoute lang="fa"><LessonDetailPage /></LocalizedRoute>} />
        <Route path="/fa/azmoon-ha" element={<LocalizedRoute lang="fa"><TestsPage /></LocalizedRoute>} />
        <Route path="/fa/alamat-rahnamaii" element={<LocalizedRoute lang="fa"><TrafficSignsPage /></LocalizedRoute>} />
        <Route path="/fa/alamat-rahnamaii/:id" element={<LocalizedRoute lang="fa"><TrafficSignDetailPage /></LocalizedRoute>} />
        <Route path="/fa/nookat-sari" element={<LocalizedRoute lang="fa"><QuickTipsPage /></LocalizedRoute>} />
        <Route path="/fa/naghshe-taamoli" element={<LocalizedRoute lang="fa"><InteractiveMapPage /></LocalizedRoute>} />
        <Route path="/fa/kalamaat-mohem" element={<LocalizedRoute lang="fa"><ImportantWordsPage /></LocalizedRoute>} />
        <Route path="/fa/rahnamaye-estefade" element={<LocalizedRoute lang="fa"><HowToUsePage /></LocalizedRoute>} />
        <Route path="/fa/maqalat" element={<LocalizedRoute lang="fa"><ArticlesPage /></LocalizedRoute>} />
        <Route path="/fa/qavanin-rahnamai" element={<LocalizedRoute lang="fa"><TrafficRulesPage /></LocalizedRoute>} />
        <Route path="/fa/nookat-sari/:id" element={<LocalizedRoute lang="fa"><QuickTipDetailPage /></LocalizedRoute>} />
        <Route path="/fa/soalat-teori/:slug" element={<LocalizedRoute lang="fa"><QuestionDetailPage /></LocalizedRoute>} />
        <Route path="/fa/poshtibani" element={<LocalizedRoute lang="fa"><SupportPage /></LocalizedRoute>} />
        <Route path="/fa/panel-karbar" element={<LocalizedRoute lang="fa"><ProtectedRoute><DashboardPage /></ProtectedRoute></LocalizedRoute>} />
        <Route path="/fa/tanzimaat" element={<LocalizedRoute lang="fa"><ProtectedRoute><SettingsPage /></ProtectedRoute></LocalizedRoute>} />
        <Route path="/fa/harim-khososi" element={<LocalizedRoute lang="fa"><PrivacyPolicyPage /></LocalizedRoute>} />
        <Route path="/fa/sharayet" element={<LocalizedRoute lang="fa"><TermsOfServicePage /></LocalizedRoute>} />
        {/* Persian backwards compatibility redirects */}
        <Route path="/fa/login" element={<Navigate to="/fa/vorod" replace />} />
        <Route path="/fa/register" element={<Navigate to="/fa/sabt-nam" replace />} />
        <Route path="/fa/lessons" element={<Navigate to="/fa/dars-ha" replace />} />
        <Route path="/fa/tests" element={<Navigate to="/fa/azmoon-ha" replace />} />
        <Route path="/fa/assistant" element={<Navigate to="/fa/dastiar" replace />} />
        <Route path="/fa/pricing" element={<Navigate to="/fa/gheymat-ha" replace />} />
        <Route path="/fa/support" element={<Navigate to="/fa/poshtibani" replace />} />
        <Route path="/fa/dashboard" element={<Navigate to="/fa/panel-karbar" replace />} />
        <Route path="/fa/traffic-signs" element={<Navigate to="/fa/alamat-rahnamaii" replace />} />
        <Route path="/fa/quick-tips" element={<Navigate to="/fa/nookat-sari" replace />} />
        <Route path="/fa/settings" element={<Navigate to="/fa/tanzimaat" replace />} />
        <Route path="/fa/privacy" element={<Navigate to="/fa/harim-khososi" replace />} />
        <Route path="/fa/terms" element={<Navigate to="/fa/sharayet" replace />} />
        
        {/* Somali Routes - All Pages (Somali paths) */}
        <Route path="/so" element={<LocalizedRoute lang="so"><LandingPage /></LocalizedRoute>} />
        <Route path="/so/gal" element={<LocalizedRoute lang="so"><LoginPage /></LocalizedRoute>} />
        <Route path="/so/diiwaangeli" element={<LocalizedRoute lang="so"><RegisterPage /></LocalizedRoute>} />
        <Route path="/so/ilowday-furaha" element={<LocalizedRoute lang="so"><ForgotPasswordPage /></LocalizedRoute>} />
        <Route path="/so/qiimaha" element={<LocalizedRoute lang="so"><PricingPage /></LocalizedRoute>} />
        <Route path="/so/kaaliye" element={<LocalizedRoute lang="so"><AssistantPage /></LocalizedRoute>} />
        <Route path="/so/casharada" element={<LocalizedRoute lang="so"><LessonsPage /></LocalizedRoute>} />
        <Route path="/so/casharada/:id" element={<LocalizedRoute lang="so"><LessonDetailPage /></LocalizedRoute>} />
        <Route path="/so/imtixaanada" element={<LocalizedRoute lang="so"><TestsPage /></LocalizedRoute>} />
        <Route path="/so/calaamadaha-taraafikada" element={<LocalizedRoute lang="so"><TrafficSignsPage /></LocalizedRoute>} />
        <Route path="/so/calaamadaha-taraafikada/:id" element={<LocalizedRoute lang="so"><TrafficSignDetailPage /></LocalizedRoute>} />
        <Route path="/so/talooyinka-degdegga" element={<LocalizedRoute lang="so"><QuickTipsPage /></LocalizedRoute>} />
        <Route path="/so/khariidad-isdhexgal" element={<LocalizedRoute lang="so"><InteractiveMapPage /></LocalizedRoute>} />
        <Route path="/so/erayada-muhiimka" element={<LocalizedRoute lang="so"><ImportantWordsPage /></LocalizedRoute>} />
        <Route path="/so/sida-loo-isticmaalo" element={<LocalizedRoute lang="so"><HowToUsePage /></LocalizedRoute>} />
        <Route path="/so/maqaallada" element={<LocalizedRoute lang="so"><ArticlesPage /></LocalizedRoute>} />
        <Route path="/so/xeerarka-taraafikada" element={<LocalizedRoute lang="so"><TrafficRulesPage /></LocalizedRoute>} />
        <Route path="/so/talooyinka-degdegga/:id" element={<LocalizedRoute lang="so"><QuickTipDetailPage /></LocalizedRoute>} />
        <Route path="/so/sualaha-tiooriga/:slug" element={<LocalizedRoute lang="so"><QuestionDetailPage /></LocalizedRoute>} />
        <Route path="/so/taageero" element={<LocalizedRoute lang="so"><SupportPage /></LocalizedRoute>} />
        <Route path="/so/guddiga" element={<LocalizedRoute lang="so"><ProtectedRoute><DashboardPage /></ProtectedRoute></LocalizedRoute>} />
        <Route path="/so/dejinta" element={<LocalizedRoute lang="so"><ProtectedRoute><SettingsPage /></ProtectedRoute></LocalizedRoute>} />
        <Route path="/so/sirta" element={<LocalizedRoute lang="so"><PrivacyPolicyPage /></LocalizedRoute>} />
        <Route path="/so/shuruudaha" element={<LocalizedRoute lang="so"><TermsOfServicePage /></LocalizedRoute>} />
        {/* Somali backwards compatibility redirects */}
        <Route path="/so/login" element={<Navigate to="/so/gal" replace />} />
        <Route path="/so/register" element={<Navigate to="/so/diiwaangeli" replace />} />
        <Route path="/so/lessons" element={<Navigate to="/so/casharada" replace />} />
        <Route path="/so/tests" element={<Navigate to="/so/imtixaanada" replace />} />
        <Route path="/so/assistant" element={<Navigate to="/so/kaaliye" replace />} />
        <Route path="/so/pricing" element={<Navigate to="/so/qiimaha" replace />} />
        <Route path="/so/support" element={<Navigate to="/so/taageero" replace />} />
        <Route path="/so/dashboard" element={<Navigate to="/so/guddiga" replace />} />
        <Route path="/so/traffic-signs" element={<Navigate to="/so/calaamadaha-taraafikada" replace />} />
        <Route path="/so/quick-tips" element={<Navigate to="/so/talooyinka-degdegga" replace />} />
        <Route path="/so/settings" element={<Navigate to="/so/dejinta" replace />} />
        <Route path="/so/privacy" element={<Navigate to="/so/sirta" replace />} />
        <Route path="/so/terms" element={<Navigate to="/so/shuruudaha" replace />} />
        
        {/* German Routes - All Pages (German paths) */}
        <Route path="/de" element={<LocalizedRoute lang="de"><LandingPage /></LocalizedRoute>} />
        <Route path="/de/anmelden" element={<LocalizedRoute lang="de"><LoginPage /></LocalizedRoute>} />
        <Route path="/de/registrieren" element={<LocalizedRoute lang="de"><RegisterPage /></LocalizedRoute>} />
        <Route path="/de/passwort-vergessen" element={<LocalizedRoute lang="de"><ForgotPasswordPage /></LocalizedRoute>} />
        <Route path="/de/preise" element={<LocalizedRoute lang="de"><PricingPage /></LocalizedRoute>} />
        <Route path="/de/assistent" element={<LocalizedRoute lang="de"><AssistantPage /></LocalizedRoute>} />
        <Route path="/de/lektionen" element={<LocalizedRoute lang="de"><LessonsPage /></LocalizedRoute>} />
        <Route path="/de/lektionen/:id" element={<LocalizedRoute lang="de"><LessonDetailPage /></LocalizedRoute>} />
        <Route path="/de/tests" element={<LocalizedRoute lang="de"><TestsPage /></LocalizedRoute>} />
        <Route path="/de/verkehrszeichen" element={<LocalizedRoute lang="de"><TrafficSignsPage /></LocalizedRoute>} />
        <Route path="/de/verkehrszeichen/:id" element={<LocalizedRoute lang="de"><TrafficSignDetailPage /></LocalizedRoute>} />
        <Route path="/de/schnelle-tipps" element={<LocalizedRoute lang="de"><QuickTipsPage /></LocalizedRoute>} />
        <Route path="/de/interaktive-karte" element={<LocalizedRoute lang="de"><InteractiveMapPage /></LocalizedRoute>} />
        <Route path="/de/wichtige-worter" element={<LocalizedRoute lang="de"><ImportantWordsPage /></LocalizedRoute>} />
        <Route path="/de/anleitung" element={<LocalizedRoute lang="de"><HowToUsePage /></LocalizedRoute>} />
        <Route path="/de/artikel" element={<LocalizedRoute lang="de"><ArticlesPage /></LocalizedRoute>} />
        <Route path="/de/verkehrsregeln" element={<LocalizedRoute lang="de"><TrafficRulesPage /></LocalizedRoute>} />
        <Route path="/de/schnelle-tipps/:id" element={<LocalizedRoute lang="de"><QuickTipDetailPage /></LocalizedRoute>} />
        <Route path="/de/theoriefragen/:slug" element={<LocalizedRoute lang="de"><QuestionDetailPage /></LocalizedRoute>} />
        <Route path="/de/support" element={<LocalizedRoute lang="de"><SupportPage /></LocalizedRoute>} />
        <Route path="/en/dashboard" element={<LocalizedRoute lang="en"><ProtectedRoute><DashboardPage /></ProtectedRoute></LocalizedRoute>} />
        <Route path="/de/dashboard" element={<LocalizedRoute lang="de"><ProtectedRoute><DashboardPage /></ProtectedRoute></LocalizedRoute>} />
        <Route path="/de/einstellungen" element={<LocalizedRoute lang="de"><ProtectedRoute><SettingsPage /></ProtectedRoute></LocalizedRoute>} />
        <Route path="/de/datenschutz" element={<LocalizedRoute lang="de"><PrivacyPolicyPage /></LocalizedRoute>} />
        <Route path="/de/bedingungen" element={<LocalizedRoute lang="de"><TermsOfServicePage /></LocalizedRoute>} />
        {/* German backwards compatibility redirects */}
        <Route path="/de/login" element={<Navigate to="/de/anmelden" replace />} />
        <Route path="/de/register" element={<Navigate to="/de/registrieren" replace />} />
        <Route path="/de/lessons" element={<Navigate to="/de/lektionen" replace />} />
        <Route path="/de/assistant" element={<Navigate to="/de/assistent" replace />} />
        <Route path="/de/pricing" element={<Navigate to="/de/preise" replace />} />
        <Route path="/de/traffic-signs" element={<Navigate to="/de/verkehrszeichen" replace />} />
        <Route path="/de/quick-tips" element={<Navigate to="/de/schnelle-tipps" replace />} />
        <Route path="/de/settings" element={<Navigate to="/de/einstellungen" replace />} />
        <Route path="/de/privacy" element={<Navigate to="/de/datenschutz" replace />} />
        <Route path="/de/terms" element={<Navigate to="/de/bedingungen" replace />} />
        
        {/* Spanish Routes - All Pages (Spanish paths) */}
        <Route path="/es" element={<LocalizedRoute lang="es"><LandingPage /></LocalizedRoute>} />
        <Route path="/es/iniciar-sesion" element={<LocalizedRoute lang="es"><LoginPage /></LocalizedRoute>} />
        <Route path="/es/registrarse" element={<LocalizedRoute lang="es"><RegisterPage /></LocalizedRoute>} />
        <Route path="/es/olvide-contrasena" element={<LocalizedRoute lang="es"><ForgotPasswordPage /></LocalizedRoute>} />
        <Route path="/es/precios" element={<LocalizedRoute lang="es"><PricingPage /></LocalizedRoute>} />
        <Route path="/es/asistente" element={<LocalizedRoute lang="es"><AssistantPage /></LocalizedRoute>} />
        <Route path="/es/lecciones" element={<LocalizedRoute lang="es"><LessonsPage /></LocalizedRoute>} />
        <Route path="/es/lecciones/:id" element={<LocalizedRoute lang="es"><LessonDetailPage /></LocalizedRoute>} />
        <Route path="/es/examenes" element={<LocalizedRoute lang="es"><TestsPage /></LocalizedRoute>} />
        <Route path="/es/senales-trafico" element={<LocalizedRoute lang="es"><TrafficSignsPage /></LocalizedRoute>} />
        <Route path="/es/senales-trafico/:id" element={<LocalizedRoute lang="es"><TrafficSignDetailPage /></LocalizedRoute>} />
        <Route path="/es/consejos-rapidos" element={<LocalizedRoute lang="es"><QuickTipsPage /></LocalizedRoute>} />
        <Route path="/es/mapa-interactivo" element={<LocalizedRoute lang="es"><InteractiveMapPage /></LocalizedRoute>} />
        <Route path="/es/palabras-importantes" element={<LocalizedRoute lang="es"><ImportantWordsPage /></LocalizedRoute>} />
        <Route path="/es/como-usar" element={<LocalizedRoute lang="es"><HowToUsePage /></LocalizedRoute>} />
        <Route path="/es/articulos" element={<LocalizedRoute lang="es"><ArticlesPage /></LocalizedRoute>} />
        <Route path="/es/normas-trafico" element={<LocalizedRoute lang="es"><TrafficRulesPage /></LocalizedRoute>} />
        <Route path="/es/consejos-rapidos/:id" element={<LocalizedRoute lang="es"><QuickTipDetailPage /></LocalizedRoute>} />
        <Route path="/es/preguntas-teoria/:slug" element={<LocalizedRoute lang="es"><QuestionDetailPage /></LocalizedRoute>} />
        <Route path="/es/soporte" element={<LocalizedRoute lang="es"><SupportPage /></LocalizedRoute>} />
        <Route path="/es/panel" element={<LocalizedRoute lang="es"><ProtectedRoute><DashboardPage /></ProtectedRoute></LocalizedRoute>} />
        <Route path="/es/configuracion" element={<LocalizedRoute lang="es"><ProtectedRoute><SettingsPage /></ProtectedRoute></LocalizedRoute>} />
        <Route path="/es/privacidad" element={<LocalizedRoute lang="es"><PrivacyPolicyPage /></LocalizedRoute>} />
        <Route path="/es/terminos" element={<LocalizedRoute lang="es"><TermsOfServicePage /></LocalizedRoute>} />
        {/* Spanish backwards compatibility redirects */}
        <Route path="/es/login" element={<Navigate to="/es/iniciar-sesion" replace />} />
        <Route path="/es/register" element={<Navigate to="/es/registrarse" replace />} />
        <Route path="/es/lessons" element={<Navigate to="/es/lecciones" replace />} />
        <Route path="/es/tests" element={<Navigate to="/es/examenes" replace />} />
        <Route path="/es/assistant" element={<Navigate to="/es/asistente" replace />} />
        <Route path="/es/pricing" element={<Navigate to="/es/precios" replace />} />
        <Route path="/es/support" element={<Navigate to="/es/soporte" replace />} />
        <Route path="/es/dashboard" element={<Navigate to="/es/panel" replace />} />
        <Route path="/es/traffic-signs" element={<Navigate to="/es/senales-trafico" replace />} />
        <Route path="/es/quick-tips" element={<Navigate to="/es/consejos-rapidos" replace />} />
        <Route path="/es/settings" element={<Navigate to="/es/configuracion" replace />} />
        <Route path="/es/privacy" element={<Navigate to="/es/privacidad" replace />} />
        <Route path="/es/terms" element={<Navigate to="/es/terminos" replace />} />
        
        {/* Polish Routes - All Pages (Polish paths) */}
        <Route path="/pl" element={<LocalizedRoute lang="pl"><LandingPage /></LocalizedRoute>} />
        <Route path="/pl/logowanie" element={<LocalizedRoute lang="pl"><LoginPage /></LocalizedRoute>} />
        <Route path="/pl/rejestracja" element={<LocalizedRoute lang="pl"><RegisterPage /></LocalizedRoute>} />
        <Route path="/pl/zapomnialem-hasla" element={<LocalizedRoute lang="pl"><ForgotPasswordPage /></LocalizedRoute>} />
        <Route path="/pl/cennik" element={<LocalizedRoute lang="pl"><PricingPage /></LocalizedRoute>} />
        <Route path="/pl/asystent" element={<LocalizedRoute lang="pl"><AssistantPage /></LocalizedRoute>} />
        <Route path="/pl/lekcje" element={<LocalizedRoute lang="pl"><LessonsPage /></LocalizedRoute>} />
        <Route path="/pl/lekcje/:id" element={<LocalizedRoute lang="pl"><LessonDetailPage /></LocalizedRoute>} />
        <Route path="/pl/testy" element={<LocalizedRoute lang="pl"><TestsPage /></LocalizedRoute>} />
        <Route path="/pl/znaki-drogowe" element={<LocalizedRoute lang="pl"><TrafficSignsPage /></LocalizedRoute>} />
        <Route path="/pl/znaki-drogowe/:id" element={<LocalizedRoute lang="pl"><TrafficSignDetailPage /></LocalizedRoute>} />
        <Route path="/pl/szybkie-porady" element={<LocalizedRoute lang="pl"><QuickTipsPage /></LocalizedRoute>} />
        <Route path="/pl/mapa-interaktywna" element={<LocalizedRoute lang="pl"><InteractiveMapPage /></LocalizedRoute>} />
        <Route path="/pl/wazne-slowa" element={<LocalizedRoute lang="pl"><ImportantWordsPage /></LocalizedRoute>} />
        <Route path="/pl/jak-korzystac" element={<LocalizedRoute lang="pl"><HowToUsePage /></LocalizedRoute>} />
        <Route path="/pl/artykuly" element={<LocalizedRoute lang="pl"><ArticlesPage /></LocalizedRoute>} />
        <Route path="/pl/przepisy-drogowe" element={<LocalizedRoute lang="pl"><TrafficRulesPage /></LocalizedRoute>} />
        <Route path="/pl/szybkie-porady/:id" element={<LocalizedRoute lang="pl"><QuickTipDetailPage /></LocalizedRoute>} />
        <Route path="/pl/pytania-teoretyczne/:slug" element={<LocalizedRoute lang="pl"><QuestionDetailPage /></LocalizedRoute>} />
        <Route path="/pl/wsparcie" element={<LocalizedRoute lang="pl"><SupportPage /></LocalizedRoute>} />
        <Route path="/pl/panel" element={<LocalizedRoute lang="pl"><ProtectedRoute><DashboardPage /></ProtectedRoute></LocalizedRoute>} />
        <Route path="/pl/ustawienia" element={<LocalizedRoute lang="pl"><ProtectedRoute><SettingsPage /></ProtectedRoute></LocalizedRoute>} />
        <Route path="/pl/prywatnosc" element={<LocalizedRoute lang="pl"><PrivacyPolicyPage /></LocalizedRoute>} />
        <Route path="/pl/regulamin" element={<LocalizedRoute lang="pl"><TermsOfServicePage /></LocalizedRoute>} />
        {/* Polish backwards compatibility redirects */}
        <Route path="/pl/login" element={<Navigate to="/pl/logowanie" replace />} />
        <Route path="/pl/register" element={<Navigate to="/pl/rejestracja" replace />} />
        <Route path="/pl/lessons" element={<Navigate to="/pl/lekcje" replace />} />
        <Route path="/pl/tests" element={<Navigate to="/pl/testy" replace />} />
        <Route path="/pl/assistant" element={<Navigate to="/pl/asystent" replace />} />
        <Route path="/pl/pricing" element={<Navigate to="/pl/cennik" replace />} />
        <Route path="/pl/support" element={<Navigate to="/pl/wsparcie" replace />} />
        <Route path="/pl/dashboard" element={<Navigate to="/pl/panel" replace />} />
        <Route path="/pl/traffic-signs" element={<Navigate to="/pl/znaki-drogowe" replace />} />
        <Route path="/pl/quick-tips" element={<Navigate to="/pl/szybkie-porady" replace />} />
        <Route path="/pl/settings" element={<Navigate to="/pl/ustawienia" replace />} />
        <Route path="/pl/privacy" element={<Navigate to="/pl/prywatnosc" replace />} />
        <Route path="/pl/terms" element={<Navigate to="/pl/regulamin" replace />} />
        
        {/* Swedish Routes - All Pages */}
        <Route path="/sv" element={<LocalizedRoute lang="sv"><LandingPage /></LocalizedRoute>} />
        <Route path="/sv/login" element={<LocalizedRoute lang="sv"><LoginPage /></LocalizedRoute>} />
        <Route path="/sv/register" element={<LocalizedRoute lang="sv"><RegisterPage /></LocalizedRoute>} />
        <Route path="/sv/forgot-password" element={<LocalizedRoute lang="sv"><ForgotPasswordPage /></LocalizedRoute>} />
        <Route path="/sv/pricing" element={<LocalizedRoute lang="sv"><PricingPage /></LocalizedRoute>} />
        <Route path="/sv/assistant" element={<LocalizedRoute lang="sv"><AssistantPage /></LocalizedRoute>} />
        <Route path="/sv/lessons" element={<LocalizedRoute lang="sv"><LessonsPage /></LocalizedRoute>} />
        <Route path="/sv/tests" element={<LocalizedRoute lang="sv"><TestsPage /></LocalizedRoute>} />
        <Route path="/sv/traffic-signs" element={<LocalizedRoute lang="sv"><TrafficSignsPage /></LocalizedRoute>} />
        <Route path="/sv/quick-tips" element={<LocalizedRoute lang="sv"><QuickTipsPage /></LocalizedRoute>} />
        <Route path="/sv/viktiga-ord" element={<LocalizedRoute lang="sv"><ImportantWordsPage /></LocalizedRoute>} />
        <Route path="/sv/hur-man-anvander" element={<LocalizedRoute lang="sv"><HowToUsePage /></LocalizedRoute>} />
        <Route path="/sv/support" element={<LocalizedRoute lang="sv"><SupportPage /></LocalizedRoute>} />
        <Route path="/sv/dashboard" element={<LocalizedRoute lang="sv"><ProtectedRoute><DashboardPage /></ProtectedRoute></LocalizedRoute>} />
        <Route path="/sv/settings" element={<LocalizedRoute lang="sv"><ProtectedRoute><SettingsPage /></ProtectedRoute></LocalizedRoute>} />
        <Route path="/sv/privacy" element={<LocalizedRoute lang="sv"><PrivacyPolicyPage /></LocalizedRoute>} />
        <Route path="/sv/terms" element={<LocalizedRoute lang="sv"><TermsOfServicePage /></LocalizedRoute>} />
        
        {/* Old localized routes - keep for backwards compatibility */}
        <Route path="/fornya-prenumeration" element={<RenewSubscriptionPage />} />
        
        {/* لوحة الإدارة */}
        <Route path="/admin-panel" element={<Navigate to="/admin" replace />} />
        <Route path="/admin" element={<ProtectedRoute><AdminPanelPage /></ProtectedRoute>} />
        
        {/* إعادة توجيه الروابط القديمة (الإنجليزية) إلى الجديدة (السويدية) */}
        <Route path="/login" element={<Navigate to="/logga-in" replace />} />
        <Route path="/register" element={<Navigate to="/registrera" replace />} />
        <Route path="/verify-email" element={<RedirectWithQuery to="/verifiera-epost" />} />
        <Route path="/forgot-password" element={<Navigate to="/glomt-losenord" replace />} />
        <Route path="/pricing" element={<Navigate to="/priser" replace />} />
        <Route path="/payment/success" element={<Navigate to="/betalning/lyckades" replace />} />
        <Route path="/tests" element={<Navigate to="/teoriprov" replace />} />
        <Route path="/dashboard" element={<Navigate to="/oversikt" replace />} />
        <Route path="/lessons" element={<Navigate to="/teorilektioner" replace />} />
        <Route path="/lessons/:id" element={<RedirectWithParams to="/teorilektioner" />} />
        <Route path="/traffic-signs" element={<Navigate to="/trafikmarken" replace />} />
        <Route path="/traffic-signs/:id" element={<RedirectWithParams to="/trafikmarken" />} />
        <Route path="/quick-tips" element={<Navigate to="/snabba-rad" replace />} />
        <Route path="/quick-tips/:id" element={<RedirectWithParams to="/snabba-rad" />} />
        <Route path="/assistant" element={<Navigate to="/ai-assistent" replace />} />
        <Route path="/settings" element={<Navigate to="/installningar" replace />} />
        <Route path="/privacy" element={<Navigate to="/integritet" replace />} />
        <Route path="/terms" element={<Navigate to="/villkor" replace />} />
        <Route path="/refund" element={<Navigate to="/aterbetalning" replace />} />
        <Route path="/disclaimer" element={<Navigate to="/ansvarsfriskrivning" replace />} />
        <Route path="/renew-subscription" element={<Navigate to="/fornya-prenumeration" replace />} />
        
        <Route path="/important-words" element={<Navigate to="/viktiga-ord" replace />} />
        
        {/* Catch-all: redirect unknown routes to home page */}
        <Route path="/sv/*" element={<SvPrefixRedirect />} />
        <Route path="*" element={<NotFoundRedirect />} />
      </Routes>
      </Suspense>
      </main>
      <Toaster position="top-center" richColors />
      <ConnectionStatus />
      <Suspense fallback={null}>
        <CookieConsent />
      </Suspense>
      <Suspense fallback={null}>
        <ChatWidget />
      </Suspense>
      <Suspense fallback={null}>
        <TrialHourCountdown lang={i18n?.language || 'sv'} />
      </Suspense>
    </div>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <ThemeProvider>
          <BrowserRouter>
            <AuthProvider>
              <AppContent />
            </AuthProvider>
          </BrowserRouter>
        </ThemeProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
